import { computed, onMounted, onUnmounted, ref } from 'vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import FortuneWheelImage from '@/assets/images/fortune-wheel.webp'
import StarImage from '@/assets/images/premium.png'
import BoxImage from '@/assets/images/mystery-boxes/mystery-boxes-main.webp'
import MineImage from '@/assets/images/special-currencies/mine.webp'
import AntiMineImage from '@/assets/images/multiplayer/mine-antimine-icon.png'
import MagnetImage from '@/assets/images/multiplayer/mine-magnet-icon.webp'
import { usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { buildDropeeStorageKey } from '@/config/config'
import { useLocalStorage } from '@vueuse/core'
import { allSpecialCurrenciesConfig, specialCurrenciesConfig } from '@/config/special-currencies'
import { useInventory } from '@/composables/game/inventory'
import { liveOpsEvents } from '@/composables/game/live-ops-events'

const MINE_ITEM_IDS = new Set(['mp.mine', 'mp.antiMine', 'mp.mineMagnet'])

const specialCollectiblesConfig: Record<string, { name: string; image: string }> = {
  'mp.mine': { name: 'Mines', image: MineImage },
  'mp.antiMine': { name: 'Anti-Mines', image: AntiMineImage },
  'mp.mineMagnet': { name: 'Magnets', image: MagnetImage },
}

export type BackpackItem = {
  id?: string
  name: string
  image: string
  value: number
  isNew?: boolean
}

const seenBackpackItems = useLocalStorage<string[]>(buildDropeeStorageKey('backpackSeenItems'), [])
const backpackItemsPrevValues = useLocalStorage<Record<string, number>>(buildDropeeStorageKey('backpackItemsPrevValues'), {})
const showBackPackOverlay = ref(false)
export const useBackPack = ({ init = false, sync = false }: { init?: boolean; sync?: boolean } = {}) => {

  if (init) {
    reset()
  }

  const { coins, player } = usePlayer()
  const { availableSpins, updateState: updateFortuneWheelState, wheelSlices, goldenWheelSlices } = useFortuneWheel()
  const { playerInventory } = useInventory()

  const activeCurrencyIds = computed(() => {
    const now = Date.now() / 1000
    const ids = new Set<string>()
    for (const event of liveOpsEvents.value) {
      if (event.endTimestamp > now && !event.isComplete) {
        for (const id of event.template.rewards) ids.add(id)
        for (const id of event.template.costs) ids.add(id)
      }
    }
    return ids
  })

  const wheelCurrencyIds = computed(() => {
    const ids = new Set<string>()
    for (const slice of [...wheelSlices.value, ...goldenWheelSlices.value]) {
      ids.add(slice.currency)
    }
    return ids
  })

  const stars = computed(() => player.value.ecommerce.starsBalance)
  const boxes = computed(() => Object.values(player.value.ecommerce.mysteryBoxes).reduce((sum, v) => sum + (v > 0 ? v : 0), 0))

  const essentials = computed((): BackpackItem[] => [
    { name: 'Coins', image: CoinIcon, value: coins.value },
    { name: 'Spins', image: FortuneWheelImage, value: availableSpins.value },
    { name: 'Stars', image: StarImage, value: stars.value },
    { name: 'Boxes', image: BoxImage, value: boxes.value },
  ].map(item => ({
    ...item,
    isNew: getIsNew(item),
  })),
  )

  const collectibles = computed((): BackpackItem[] =>
    Object.entries(allSpecialCurrenciesConfig.value)
      .filter(([id]) => {
        return !MINE_ITEM_IDS.has(id) && 
          (id in specialCurrenciesConfig
          ? wheelCurrencyIds.value.has(id)
          : activeCurrencyIds.value.has(id))
      })
      .map(([id, config]) => ({
        id,
        name: config.label,
        image: config.image,
        value: (player.value.specialCurrencies as Record<string, number>)[id] ?? 0,
      })).map(item => ({
        ...item,
        isNew: getIsNew(item),
      })).filter(item => item.value > 0),
  )

  const specialCollectibles = computed((): BackpackItem[] =>
    Object.entries(specialCollectiblesConfig).map(([id, config]) => ({
      id,
      name: config.name,
      image: config.image,
      value: (player.value.specialCurrencies as Record<string, number>)[id] ?? 0,
      isNew: false,
    })).filter(item => item.value > 0),
  )

  const specials = computed((): BackpackItem[] =>
    playerInventory.value.map(item => ({
      name: item.name,
      image: item.imageUrl,
      value: item.count,
      isNew: getIsNew({ name: item.name, image: item.imageUrl, value: item.count }),
    })),
  )

  const isThereAnySpecial = computed(() => {
    return specials.value.some(item => item.value > 0)
  })

  const newItemsCount = computed(() => {
    return [
      ...essentials.value,
      ...collectibles.value,
      ...specialCollectibles.value,
      ...specials.value,
    ].filter(item => item.isNew).length
  })

  function getIsNew(item: BackpackItem) {
    const prev = backpackItemsPrevValues.value[item.name] ?? 0
    return item.value > 0 && prev === 0 && !seenBackpackItems.value.includes(item.name)
  }

  function closeBackpack() {

    const allItems = [
      ...essentials.value,
      ...collectibles.value,
      ...specialCollectibles.value,
      ...specials.value,
    ]
    const newSeen = new Set(seenBackpackItems.value)
    const newPrev = { ...backpackItemsPrevValues.value }
    allItems.forEach(item => {
      if (item.isNew) {
        newSeen.add(item.name)
        newPrev[item.name] = item.value // update prev only when seen
      }
    })
    seenBackpackItems.value = Array.from(newSeen)
    backpackItemsPrevValues.value = newPrev
  }

  function reset() {
    seenBackpackItems.value = []
    backpackItemsPrevValues.value = {}
  }

  onMounted(async () => {
    if (sync) {
      await Promise.all([updateFortuneWheelState()])
    }
  })

  onUnmounted(() => closeBackpack())

  return {
    essentials,
    collectibles,
    specialCollectibles,
    specials,
    isThereAnySpecial,
    stars,
    coins,
    availableSpins,
    player,
    newItemsCount,
    reset,
    showBackPackOverlay,
  }
}
